'use client';

import { useEffect, useMemo, useState } from 'react';
import { Pause, Play, ExternalLink, Globe, Image } from 'lucide-react';
import type { Post } from '@/types';
import { getFrozenFirehosePosts } from '@/lib/api';
import { formatTimestamp, getBlueskyPostUrl, truncateText } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function parseTextWithLinks(text: string): React.ReactNode[] {
  const combinedRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)|(@[\w.-]+)|(\#[\w\u00C0-\u024F]+)/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    const [fullMatch] = match;
    const key = `${match.index}-${fullMatch}`;

    if (fullMatch.startsWith('http')) {
      result.push(
        <a
          key={key}
          href={fullMatch}
          target="_blank"
          rel="noopener noreferrer"
          className="text-main hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {fullMatch}
        </a>
      );
    } else if (fullMatch.startsWith('@')) {
      const handle = fullMatch.slice(1);
      result.push(
        <a
          key={key}
          href={`https://bsky.app/profile/${handle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-main hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {fullMatch}
        </a>
      );
    } else if (fullMatch.startsWith('#')) {
      const tag = fullMatch.slice(1);
      result.push(
        <a
          key={key}
          href={`https://bsky.app/search?q=%23${encodeURIComponent(tag)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-main hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {fullMatch}
        </a>
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result.length > 0 ? result : [text];
}

const ENABLE_IMAGES = false;

export function FirehosePanel() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [paused, setPaused] = useState(false);
  const [imagesOnly, setImagesOnly] = useState(false);

  useEffect(() => {
    let mounted = true;
    void getFrozenFirehosePosts().then((data) => {
      if (mounted) {
        setPosts(data);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const displayedPosts = useMemo(() => {
    if (paused) return [];
    return posts
      .filter((post) => !ENABLE_IMAGES || !imagesOnly || (post.image_urls && post.image_urls.length > 0))
      .filter((post) => !post.labels?.some((l) => ['porn', 'nudity', 'sexual', 'nsfw'].includes(l.toLowerCase())));
  }, [imagesOnly, paused, posts]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b-2 border-border bg-secondary-background">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-heading text-foreground">Frozen Firehose Snapshot</h2>
          <Button variant={paused ? 'default' : 'neutral'} size="sm" onClick={() => setPaused((prev) => !prev)}>
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            {paused ? 'Show Posts' : 'Hide Posts'}
          </Button>
          {ENABLE_IMAGES && (
            <Button variant={imagesOnly ? 'default' : 'neutral'} size="sm" onClick={() => setImagesOnly((prev) => !prev)}>
              <Image className="w-4 h-4" />
              Images Only
            </Button>
          )}
        </div>

        <div className="flex items-center gap-6 text-sm font-base">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
            <span className="text-foreground/70">Frozen snapshot (no live stream)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-foreground/70">Posts captured:</span>
            <span className="font-mono font-heading text-foreground">{posts.length}</span>
          </div>
          <Badge variant="neutral">Read-only</Badge>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {displayedPosts.length === 0 ? (
          <div className="flex items-center justify-center h-full text-foreground/50">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-main/20 border-2 border-border rounded-base flex items-center justify-center">
                <span className="text-2xl">❄</span>
              </div>
              <p className="font-base">No posts shown in frozen mode.</p>
            </div>
          </div>
        ) : (
          <div className="divide-y-2 divide-border">
            {displayedPosts.map((post, index) => (
              <div key={`${post.uri}-${index}`} className="p-4 hover:bg-main/10">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-heading text-foreground text-sm truncate">
                        @{post.handle || truncateText(post.repo_did, 16)}
                      </span>
                      <span className="text-foreground/50 text-xs">·</span>
                      <span className="text-foreground/50 text-xs font-base">{formatTimestamp(post.created_at)}</span>
                      {post.langs && post.langs.length > 0 && (
                        <Badge variant="neutral" className="text-xs py-0">
                          {post.langs[0]}
                        </Badge>
                      )}
                    </div>
                    <p className="text-foreground text-sm font-base whitespace-pre-wrap break-words line-clamp-3">
                      {parseTextWithLinks(post.text)}
                    </p>
                    {post.link_domains && post.link_domains.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-xs">
                        <Globe className="w-3 h-3 text-foreground/50" />
                        {post.link_domains.map((domain, i) => (
                          <a
                            key={i}
                            href={`https://${domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-main hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {domain}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <a
                    href={getBlueskyPostUrl(post.uri)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-main hover:text-main/80 flex-shrink-0"
                    title="Open on Bluesky"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
